import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

/* ================= STATUS HELPERS ================= */

const statusColorMap = (status) => {
  switch (status) {
    case 'A': return 'error';
    case 'PH':
    case 'W/O': return 'info';
    case 'WOS':
    case 'P': return 'success';
    case 'HD': return 'warning';
    case 'PL':
    case 'L': return 'warning';
    default: return 'default';
  }
};

const statusLabelMap = (status) => {
  switch (status) {
    case 'A': return 'Absent';
    case 'PH': return 'Paid Holiday';
    case 'W/O': return 'Week Off';
    case 'WOS': return 'Worked on Sunday';
    case 'P': return 'Present';
    case 'PL': return 'Paid Leave';
    case 'L': return 'Leave';
    case 'HD': return 'Half Day';
    default: return status || '-';
  }
};

/* ================= COMPONENT ================= */

const EmployeeDailyPay = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    monthlySalary: 0,
    totalEarned: 0,
    monthlyIncentive: 0,
    monthlyOtherAllowance: 0
  });

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!user?.employeeId) return;

    // ✅ SINGLE SOURCE OF TRUTH
    const referenceDate = new Date().toISOString().split('T')[0];

    setLoading(true);

    api
      .get(
        `/payroll/daily-breakdown/${user.employeeId}?referenceDate=${referenceDate}`
      )
      .then(res => {
        const data = res.data || {};

        setRows(Array.isArray(data.dailyBreakdown) ? data.dailyBreakdown : []);

        setSummary({
          monthlySalary: Number(data.monthlySalary || 0),
          totalEarned: Number(data.earnedSalary || 0),
          monthlyIncentive: Number(data.incentive || 0),
          monthlyOtherAllowance: 0 // informational only
        });
      })
      .catch(err => {
        console.error('Failed to load daily payroll', err);
        setRows([]);
        setSummary({
          monthlySalary: 0,
          totalEarned: 0,
          monthlyIncentive: 0,
          monthlyOtherAllowance: 0
        });
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        My Daily Pay
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Payroll cycle: 21st to 20th
      </Typography>

      {/* SUMMARY */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Typography variant="overline">Monthly Salary</Typography>
            <Typography variant="h5" fontWeight={800}>
              ₹ {summary.monthlySalary.toLocaleString('en-IN')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="overline" color="primary">
              Earned (This Cycle)
            </Typography>
            <Typography variant="h5" fontWeight={800} color="primary">
              ₹ {summary.totalEarned.toLocaleString('en-IN')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="overline" color="success.main">
              Incentives
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              ₹ {summary.monthlyIncentive.toLocaleString('en-IN')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="overline" color="warning.main">
              Other Allowances
            </Typography>
            <Typography variant="h6" fontWeight={700} color="warning.main">
              ₹ {summary.monthlyOtherAllowance.toLocaleString('en-IN')}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box display="flex" alignItems="center" gap={2}>
          <TrendingUpIcon color="success" sx={{ fontSize: 36 }} />
          <Typography fontWeight={600}>
            Incentives are added to salary. Other allowances are informational.
          </Typography>
        </Box>
      </Paper>

      {/* DAILY TABLE */}
      <Paper elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>Date</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell align="right"><b>Earned (₹)</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>
                    {new Date(r.date).toLocaleDateString('en-GB')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabelMap(r.status)}
                      color={statusColorMap(r.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    ₹ {Number(r.earned || 0).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default EmployeeDailyPay;
